import config from "../../_helpers/config";
import { apiFetcher } from "./apiFetcher";
import { versioningWixService } from "./versioning.wix.service";
import _ from "lodash";

export const productsService = {
  getProducts,
  getProductById,
};

// V3 Search uses cursor paging while the picker (material-table) navigates by page.
// Cache the "next" cursor per query+page so sequential paging resolves. Keyed by
// `${search}|${sort}`; reset implicitly when the key changes.
const v3CursorCaches = {};

const V3_LIST_FIELDS = ['CURRENCY', 'MEDIA_ITEMS_INFO'];
const V3_DETAIL_FIELDS = [
  'CURRENCY',
  'MEDIA_ITEMS_INFO',
  'PLAIN_DESCRIPTION',
  'VARIANT_OPTION_CHOICE_NAMES',
  'PRODUCT_CHOICES_MEDIA_REFERENCES',
];

function getProducts(query) {
  return versioningWixService.getCatalogVersion().then((version) =>
    version === 'V3' ? getProductsV3(query) : getProductsV1(query),
  );
}

function getProductById(productId) {
  return versioningWixService.getCatalogVersion().then((version) =>
    version === 'V3' ? getProductByIdV3(productId) : getProductByIdV1(productId),
  );
}

// ─── V1 (unchanged) ─────────────────────────────────────────────────────────────

function getProductsV1(query) {
  const url = `stores/v1/products/query`;

  let body = {
    query: query,
    includeVariants: true,
  };

  if (!query.paging?.limit) {
    body.query.paging = { ...body.query.paging, limit: config.PRODUCTS_PER_PAGE };
  }

  return apiFetcher(url, { method: "POST", body: JSON.stringify(body) }, 1, true);
}

function getProductByIdV1(productId) {
  const url = `stores/v1/products/${productId}`;
  return apiFetcher(url, { method: "GET" });
}

// ─── V3 ───────────────────────────────────────────────────────────────────────

/** Parse the picker's JSON-string filter (e.g. `{"name":{"$contains":"x"}}`). */
function parseFilter(filter) {
  if (!filter) return {};
  if (typeof filter === 'object') return filter;
  try {
    return JSON.parse(filter) || {};
  } catch {
    return {};
  }
}

/** Translate the picker's `[{ field: dir }]` sort (JSON string) into a V3 sort spec. */
function buildV3Sort(sortStr) {
  let parsed = [];
  try {
    parsed = JSON.parse(sortStr) || [];
  } catch {
    parsed = [];
  }
  const first = parsed[0] || { name: 'asc' };
  const field = Object.keys(first)[0] || 'name';
  const order = String(first[field]).toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
  const fieldName = field === 'price' ? 'actualPriceRange.minValue.amount' : field;
  return [{ fieldName, order }];
}

function getProductsV3(query) {
  // The query shape varies by caller: the Wix carousel/picker use `paging.{limit,offset}`,
  // while the ProductList section falls through to a generic `{ per_page }` shape (there is
  // no Wix case in its switch). `config.PRODUCTS_PER_PAGE` is undefined, so fall back
  // through the known shapes to a concrete default.
  const limit = _.get(query, 'paging.limit') || query.per_page || query.limit || 30;
  const offset = _.get(query, 'paging.offset', 0);
  const page = Math.floor(offset / limit) + 1;
  const sortSpec = buildV3Sort(query.sort);

  const filterObj = parseFilter(query.filter);
  const searchTerm = _.get(filterObj, 'name.$contains', '');
  // The category id arrives in a few shapes depending on the caller: `category_id` on the
  // query object (previewer sections), the flat V1 filter key `collections.id` (carousel),
  // or `collectionIds.$hasSome` (legacy). Accept all of them.
  const categoryId =
    query.category_id ||
    filterObj['collections.id'] ||
    _.get(filterObj, 'collectionIds.$hasSome[0]');

  const cacheKey = `${searchTerm}|${categoryId || ''}|${JSON.stringify(sortSpec)}`;
  const cursors = v3CursorCaches[cacheKey] || [null];
  const cursor = cursors[page - 1];

  if (cursor === undefined) {
    return Promise.resolve({
      data: { products: [], totalResults: (page - 1) * limit, __catalogVersion: 'V3' },
      headers: new Headers(),
    });
  }

  const filter = { visible: { $eq: true } };
  if (categoryId) {
    // Each element of `directCategoriesInfo.categories` is identified by `id`.
    filter['directCategoriesInfo.categories'] = { $matchItems: [{ id: categoryId }] };
  }

  // Wix rejects filter/sort/search alongside a cursor (SE-1141) — the cursor already
  // encodes them. Send the full query only on the first page; the cursor alone after.
  let search;
  if (cursor) {
    search = { cursorPaging: { limit, cursor } };
  } else {
    search = {
      cursorPaging: { limit },
      filter,
      sort: sortSpec,
    };
    if (searchTerm) {
      search.search = { expression: searchTerm, fields: ['name'], mode: 'OR', fuzzy: true };
    }
  }

  const body = { fields: V3_LIST_FIELDS, search };

  return apiFetcher(
    `stores/v3/products/search`,
    { method: "POST", body: JSON.stringify(body) },
    1,
    true,
  ).then((response) => {
    const data = response.data || {};
    const next = _.get(data, 'pagingMetadata.cursors.next', null);
    const hasNext = Boolean(_.get(data, 'pagingMetadata.hasNext') && next);

    const updated = cursors.slice(0, page);
    updated[page] = next;
    v3CursorCaches[cacheKey] = updated;

    const count = _.get(data, 'products.length', 0);
    const totalResults = hasNext ? page * limit + 1 : offset + count;

    return {
      data: { products: data.products || [], totalResults, __catalogVersion: 'V3' },
      headers: response.headers,
    };
  });
}

function getProductByIdV3(productId) {
  // Wix expects `fields` as REPEATED query params (?fields=A&fields=B), not comma-separated.
  const fieldsQuery = V3_DETAIL_FIELDS.map((f) => `fields=${f}`).join('&');
  const url = `stores/v3/products/${productId}?${fieldsQuery}`;
  return apiFetcher(url, { method: "GET" }).then((response) => ({
    ...response,
    __catalogVersion: 'V3',
  }));
}
